import { isNil } from 'lodash'

import { getTokenCookies } from 'features/user'
import { authUserFromDTO } from 'features/user/api/authUser'
import { authenticate, reset } from 'features/user/store/userSlice'
import { store } from 'providers/redux'
import { request } from 'providers/tanstack-query/client'
import { handleResponse200 } from 'providers/tanstack-query/response'
import { getKey } from 'providers/tanstack-query/utils'
import { REFRESH_TOKEN } from './mutation'
import { removeTokenCookies } from './tokenCookies'

import type { RefreshTokenData, RefreshTokenResult, RefreshTokenVars } from './types'
import type { Ability } from 'providers/casl'
import type { GraphQlResponse } from 'providers/tanstack-query/response'

/**
 * Remember to catch Promise errors when using this function because it does not handle them internally.
 */
export const refreshToken = (ability: Ability): Promise<RefreshTokenResult> => {
  const { refreshToken } = getTokenCookies()

  if (isNil(refreshToken)) {
    store.dispatch(reset())

    return Promise.resolve(undefined)
  }

  return request<RefreshTokenVars, RefreshTokenData>({
    key: getKey(REFRESH_TOKEN(ability)),
    query: REFRESH_TOKEN(ability),
    variables: { refreshToken },
  }).then((response: GraphQlResponse<RefreshTokenData> | void) => {
    const data: RefreshTokenData | undefined = handleResponse200<RefreshTokenData>({ response })

    if (data === undefined) {
      return undefined
    }

    const {
      refreshToken: { accessToken, errors, refreshToken, user },
    } = data

    const hasErrors: boolean = !isNil(errors) && errors.length > 0

    if (isNil(accessToken) || isNil(refreshToken) || isNil(user) || hasErrors) {
      removeTokenCookies()
      window.location.reload()
      return undefined
    }

    store.dispatch(
      authenticate({
        accessToken,
        refreshToken,
        user: authUserFromDTO(user),
      }),
    )

    return { accessToken, refreshToken }
  })
}
